import React, {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import cn from "classnames";
import styles from "./custom-tooltip.module.scss";
import { ToolTipPositionType } from "@src/components/atoms/tooltip/tooltip";

interface CustomTooltipProps {
  content: string;
  children: React.ReactNode;
  position?: ToolTipPositionType;
  maxWidth?: string;
  className?: string;
}

interface TooltipLayout {
  top: number;
  left: number;
  maxWidth: number;
  maxHeight: number;
}

const VIEWPORT_MARGIN = 8;
const TOOLTIP_GAP = 8;
type TooltipSide = "top" | "right" | "bottom" | "left";
type TooltipAlignment = "left" | "center" | "right";

const calculateTooltipLayout = (
  anchor: DOMRect,
  tooltip: DOMRect,
  preferredPosition: ToolTipPositionType,
): TooltipLayout => {
  const [preferredSide, ...alignmentParts] = preferredPosition.split("-");
  let side = preferredSide as TooltipSide;
  const alignment: TooltipAlignment = alignmentParts.includes("left")
    ? "left"
    : alignmentParts.includes("right")
    ? "right"
    : "center";

  const available = {
    top: Math.max(0, anchor.top - VIEWPORT_MARGIN),
    bottom: Math.max(0, window.innerHeight - anchor.bottom - VIEWPORT_MARGIN),
    left: Math.max(0, anchor.left - VIEWPORT_MARGIN),
    right: Math.max(0, window.innerWidth - anchor.right - VIEWPORT_MARGIN),
  };
  const fits = (candidate: TooltipSide) =>
    available[candidate] >=
    (candidate === "top" || candidate === "bottom"
      ? tooltip.height
      : tooltip.width) +
      TOOLTIP_GAP;
  const opposite: Record<TooltipSide, TooltipSide> = {
    top: "bottom",
    bottom: "top",
    left: "right",
    right: "left",
  };

  if (!fits(side)) {
    const otherSide = opposite[side];
    if (fits(otherSide) || available[otherSide] >= available[side]) {
      side = otherSide;
    }
  }

  const isVertical = side === "top" || side === "bottom";
  const availableWidth = isVertical
    ? window.innerWidth - VIEWPORT_MARGIN * 2
    : available[side] - TOOLTIP_GAP;
  const availableHeight = isVertical
    ? available[side] - TOOLTIP_GAP
    : window.innerHeight - VIEWPORT_MARGIN * 2;
  const maxWidth = Math.max(1, Math.min(tooltip.width, availableWidth));
  const maxHeight = Math.max(1, Math.min(tooltip.height, availableHeight));
  const width = Math.min(tooltip.width, maxWidth);
  const height = Math.min(tooltip.height, maxHeight);

  let left: number;
  let top: number;
  if (isVertical) {
    left =
      alignment === "left"
        ? anchor.left
        : alignment === "right"
        ? anchor.right - width
        : anchor.left + (anchor.width - width) / 2;
    top =
      side === "top"
        ? anchor.top - height - TOOLTIP_GAP
        : anchor.bottom + TOOLTIP_GAP;
  } else {
    left =
      side === "left"
        ? anchor.left - width - TOOLTIP_GAP
        : anchor.right + TOOLTIP_GAP;
    top = anchor.top + (anchor.height - height) / 2;
  }

  return {
    left: Math.min(
      Math.max(left, VIEWPORT_MARGIN),
      Math.max(VIEWPORT_MARGIN, window.innerWidth - width - VIEWPORT_MARGIN),
    ),
    top: Math.min(
      Math.max(top, VIEWPORT_MARGIN),
      Math.max(VIEWPORT_MARGIN, window.innerHeight - height - VIEWPORT_MARGIN),
    ),
    maxWidth,
    maxHeight,
  };
};

export const CustomTooltip = ({
  content,
  children,
  position = "top",
  maxWidth = "250px",
  className,
}: CustomTooltipProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const isHoveredRef = useRef(false);
  const tooltipId = useId();
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const [layout, setLayout] = useState<TooltipLayout | null>(null);
  const [viewportRevision, setViewportRevision] = useState(0);

  useLayoutEffect(() => {
    if (!anchorRect) {
      setLayout(null);
      return;
    }

    const tooltip = tooltipRef.current;
    if (!tooltip) return;

    // Measure without the previous constraints so a larger viewport can expand
    // the tooltip again after an earlier resize.
    tooltip.style.maxWidth = `min(${maxWidth}, calc(100vw - ${
      VIEWPORT_MARGIN * 2
    }px))`;
    tooltip.style.maxHeight = `calc(100vh - ${VIEWPORT_MARGIN * 2}px)`;
    const unconstrainedRect = tooltip.getBoundingClientRect();
    const firstLayout = calculateTooltipLayout(
      anchorRect,
      unconstrainedRect,
      position,
    );

    // Measure again after applying side-specific limits so wrapping and scrolling
    // are included in the final coordinates.
    tooltip.style.maxWidth = `${firstLayout.maxWidth}px`;
    tooltip.style.maxHeight = `${firstLayout.maxHeight}px`;
    const nextLayout = calculateTooltipLayout(
      anchorRect,
      tooltip.getBoundingClientRect(),
      position,
    );

    // Keep measured styles in sync even when React sees no style diff.
    tooltip.style.top = `${nextLayout.top}px`;
    tooltip.style.left = `${nextLayout.left}px`;
    tooltip.style.maxWidth = `${nextLayout.maxWidth}px`;
    tooltip.style.maxHeight = `${nextLayout.maxHeight}px`;
    tooltip.style.visibility = "visible";
    setLayout(nextLayout);
  }, [anchorRect, content, maxWidth, position, viewportRevision]);

  const isOpen = anchorRect !== null;

  useEffect(() => {
    if (!isOpen) return;

    let animationFrame = 0;
    const updateAnchor = () => {
      cancelAnimationFrame(animationFrame);
      animationFrame = requestAnimationFrame(() => {
        const rect = wrapperRef.current?.getBoundingClientRect();
        if (rect) {
          setAnchorRect((previous) =>
            previous &&
            previous.top === rect.top &&
            previous.left === rect.left &&
            previous.width === rect.width &&
            previous.height === rect.height
              ? previous
              : rect,
          );
        }
      });
    };
    const updateViewport = () => {
      const rect = wrapperRef.current?.getBoundingClientRect();
      if (rect) setAnchorRect(rect);
      setViewportRevision((revision) => revision + 1);
    };
    window.addEventListener("resize", updateViewport);
    window.addEventListener("scroll", updateAnchor, true);
    return () => {
      cancelAnimationFrame(animationFrame);
      window.removeEventListener("resize", updateViewport);
      window.removeEventListener("scroll", updateAnchor, true);
    };
  }, [isOpen]);

  if (!content) return <>{children}</>;

  const showTooltip = () => {
    const rect = wrapperRef.current?.getBoundingClientRect();
    if (rect) {
      setLayout(null);
      setAnchorRect(rect);
    }
  };
  const addTooltipDescription = (target: EventTarget | null) => {
    if (!(target instanceof HTMLElement)) return;
    const trigger = target.closest<HTMLElement>(
      'button, a, input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    if (!trigger) return;
    const ids = trigger.getAttribute("aria-describedby")?.split(" ") ?? [];
    if (!ids.includes(tooltipId)) {
      trigger.setAttribute("aria-describedby", [...ids, tooltipId].join(" "));
    }
  };
  const removeTooltipDescription = (target: EventTarget | null) => {
    if (!(target instanceof HTMLElement)) return;
    const trigger = target.closest<HTMLElement>(
      'button, a, input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    if (!trigger) return;
    const ids = trigger
      .getAttribute("aria-describedby")
      ?.split(" ")
      .filter((id) => id !== tooltipId);
    if (ids?.length) {
      trigger.setAttribute("aria-describedby", ids.join(" "));
    } else {
      trigger.removeAttribute("aria-describedby");
    }
  };

  return (
    <div
      ref={wrapperRef}
      className={cn(styles.tooltip, className)}
      onMouseEnter={() => {
        isHoveredRef.current = true;
        showTooltip();
      }}
      onMouseLeave={() => {
        isHoveredRef.current = false;
        if (!wrapperRef.current?.contains(document.activeElement)) {
          setAnchorRect(null);
          setLayout(null);
        }
      }}
      onFocusCapture={(event) => {
        showTooltip();
        addTooltipDescription(event.target);
      }}
      onBlurCapture={(event) => {
        removeTooltipDescription(event.target);
        if (
          !event.currentTarget.contains(event.relatedTarget as Node | null) &&
          !isHoveredRef.current
        ) {
          setAnchorRect(null);
          setLayout(null);
        }
      }}
    >
      {children}
      {anchorRect &&
        ReactDOM.createPortal(
          <span
            ref={tooltipRef}
            id={tooltipId}
            role="tooltip"
            className={cn(
              styles.tooltip__tooltiptext,
              styles["tooltip__tooltiptext--visible"],
            )}
            style={{
              whiteSpace: "pre-line",
              overflowY: "auto",
              top: layout?.top ?? 0,
              left: layout?.left ?? 0,
              maxWidth:
                layout?.maxWidth ??
                `min(${maxWidth}, calc(100vw - ${VIEWPORT_MARGIN * 2}px))`,
              maxHeight:
                layout?.maxHeight ?? `calc(100vh - ${VIEWPORT_MARGIN * 2}px)`,
              visibility: layout ? "visible" : "hidden",
            }}
          >
            {content}
          </span>,
          document.body,
        )}
    </div>
  );
};
